// src/pages/MyRankings_Cards.jsx
import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import "../App.css";
import InfiniteRunnersDecks from "../components/InfiniteRunnerDecks.jsx";
import Update from "./sql/UpdateSnapRanking.jsx";
import { groupTheme } from "../components/utils/groupTheme.jsx";
import Card from "../components/Card.jsx";

function MyRankings_Cards({ user }) {
  const [rankings, setRankings] = useState([]);
  const [hiddenCards, setHiddenCards] = useState([]);
  const [showHiddenModal, setShowHiddenModal] = useState(false);
  const [selectedInfo, setSelectedInfo] = useState(null);
  const [selectedRank, setSelectedRank] = useState(null);
  const [updatedRank, setUpdatedRank] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [numInfinite, setNumInfinite] = useState(0);

  // === Fetch Visible Rankings ===
  useEffect(() => {
    if (!user) return;
    axios
      .get(`${process.env.REACT_APP_API_URL}api/data/snapRankings/${user.id}`)
      .then((res) => {
        const mapped = res.data.map(mapRanking);
        mapped.sort((a, b) => b.avgScore - a.avgScore);
        setRankings(mapped);
      })
      .catch((err) => console.error("Failed to fetch rankings:", err));
  }, [user]);

  // === Helper to Map Rankings ===
  const mapRanking = (r) => {
    const relianceScore = r.reliancescore;
    const variantsScore = r.variantsscore;
    const usabilityScore = r.usabilityscore;
    const avgScore = (relianceScore + variantsScore + usabilityScore) / 3;
    return {
      ...r,
      relianceScore,
      variantsScore,
      usabilityScore,
      cardName: r.cardname,
      cardHTML: r.cardhtml,
      cardDesc: r.carddesc,
      customHTML: r.customhtml,
      avgScore,
    };
  };

  // === Hidden Card Handling ===
  const fetchHiddenCards = async () => {
    if (!user) return;
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}api/data/snapRankings/hidden/${user.id}`
      );
      setHiddenCards(res.data.map(mapRanking));
    } catch (err) {
      console.error("Failed to fetch hidden cards:", err);
    }
  };

  const openHiddenModal = () => {
    fetchHiddenCards();
    setShowHiddenModal(true);
  };

  const handleUnhide = async (id) => {
    try {
      const cardToUpdate = hiddenCards.find((h) => h.id === id);
      await axios.put(`${process.env.REACT_APP_API_URL}api/updateSnapRanking/${id}`, {
        relianceScore: cardToUpdate.reliancescore,
        variantsScore: cardToUpdate.variantsscore,
        usabilityScore: cardToUpdate.usabilityscore,
        customHTML: cardToUpdate.customhtml,
        hidden: false,
      });
      await fetchHiddenCards();
      const refreshed = await axios.get(
        `${process.env.REACT_APP_API_URL}api/data/snapRankings/${user.id}`
      );
      const mapped = refreshed.data.map(mapRanking);
      mapped.sort((a, b) => b.avgScore - a.avgScore);
      setRankings(mapped);
    } catch (err) {
      console.error("Failed to unhide card:", err);
    }
  };

  const handleHideCard = async (item) => {
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}api/updateSnapRanking/${item.id}`, {
        relianceScore: item.relianceScore,
        variantsScore: item.variantsScore,
        usabilityScore: item.usabilityScore,
        customHTML: item.customHTML,
        hidden: true,
      });
      const refreshed = await axios.get(
        `${process.env.REACT_APP_API_URL}api/data/snapRankings/${user.id}`
      );
      const mapped = refreshed.data.map(mapRanking);
      mapped.sort((a, b) => b.avgScore - a.avgScore);
      setRankings(mapped);
    } catch (err) {
      console.error("Failed to hide card:", err);
    }
  };

  // === Sorting, Filtering, Grouping ===
  const withAvg = useMemo(
    () =>
      rankings
        .filter((r) => !r.hidden)
        .map((r) => ({
          ...r,
          avgScore: (r.relianceScore + r.variantsScore + r.usabilityScore) / 3,
        })),
    [rankings]
  );

  const TOP_ROW_ITEMS = useMemo(() => {
    const sorted = [...withAvg]
      .sort((a, b) => b.avgScore - a.avgScore)
      .slice(0, 3);

    return [sorted[1], sorted[0], sorted[2]].filter(Boolean);
  }, [withAvg]);

  const TOP_ROW_LABELS = useMemo(
    () => TOP_ROW_ITEMS.map((r) => r.cardName),
    [TOP_ROW_ITEMS]
  );

  const filtered = useMemo(() => {
    console.log(withAvg)
    if (!searchTerm.trim()) return withAvg;
    return withAvg.filter((r) =>
      r.cardName.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [withAvg, searchTerm, numInfinite]);

  const BUCKET_ORDER = [
    "Best of the Best",
    "Top Tier",
    "Great",
    "Solid",
    "Good",
    "Average",
    "Mediocre",
    "Not Good",
    "Bad",
    "Terrible",
    "Complete Trash", 
  ];

  const groupedData = useMemo(() => {
    const grouped = {};
    filtered
      .filter((item) => !TOP_ROW_LABELS.includes(item.cardName))
      .forEach((item) => {
        const avg = item.avgScore;
        let bucket = "Average";
        if (avg >= 95) bucket = "Best of the Best";
        else if (avg >= 90) bucket = "Top Tier";
        else if (avg >= 85) bucket = "Great";
        else if (avg >= 80) bucket = "Solid";
        else if (avg >= 75) bucket = "Good";
        else if (avg >= 70) bucket = "Average";
        else if (avg >= 65) bucket = "Mediocre";
        else if (avg >= 60) bucket = "Not Good";
        else if (avg >= 55) bucket = "Bad";
        else if (avg >= 50) bucket = "Terrible";
        else bucket = "Complete Trash";
        if (!grouped[bucket]) grouped[bucket] = [];
        grouped[bucket].push(item);
      });
    return grouped;
  }, [filtered, TOP_ROW_LABELS]);

  return (
    <div>
      {/* ====== View Hidden Cards Button ====== */}
      <div style={{ textAlign: "center", marginBottom: "16px" }}>
        <button className="button" onClick={openHiddenModal}>
          View Hidden Cards
        </button>
      </div>

      {/* ====== Hidden Cards Modal ====== */}
      {showHiddenModal && (
        <div className="modal-overlay" onClick={() => setShowHiddenModal(false)}>
          <div
            className="modal-content"
            style={{
              maxWidth: "700px",
              maxHeight: "80vh",
              overflowY: "auto",
              background: "#111",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ textAlign: "center", color: "rgb(197,176,55)" }}>
              Hidden Cards
            </h2>
            {hiddenCards.length === 0 ? (
              <p style={{ textAlign: "center", color: "#aaa" }}>
                No hidden cards found.
              </p>
            ) : (
              <div
                className="card-grid"
                style={{ gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))" }}
              >
                {hiddenCards.map((item) => (
                  <div
                    key={item.id}
                    style={{
                      background: "rgba(255,255,255,0.03)",
                      border: "1px solid rgba(255,255,255,0.1)",
                      borderRadius: "10px",
                      padding: "10px",
                      textAlign: "center",
                    }}
                  >
                    <img
                      src={
                        item.customhtml && item.customhtml.trim() !== ""
                          ? item.customhtml
                          : item.cardhtml
                      }
                      alt={item.cardName}
                      style={{
                        width: "100%",
                        borderRadius: "8px",
                        marginBottom: "8px",
                      }}
                    />
                    <button
                      className="button"
                      style={{
                        fontSize: "0.85rem",
                        padding: "4px 10px",
                        marginTop: "4px",
                      }}
                      onClick={() => handleUnhide(item.id)}
                    >
                      Unhide
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div style={{ textAlign: "center", marginTop: "16px" }}>
              <button className="button" onClick={() => setShowHiddenModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ====== Top 3 Section ====== */}
      {TOP_ROW_ITEMS.length > 0 && (
        <div className="top3-wrap">
          {TOP_ROW_ITEMS.map((item, idx) => {
            const theme = groupTheme(item.cardName);

            // Desktop pyramid order: left, center, right (center is raised)
            // You already re-ordered items earlier, so we just use idx positions.
            const desktopOffsets = [48, 0, 48]; // left/right lower than center
            const mt = TOP_ROW_ITEMS.length === 3 ? desktopOffsets[idx] : 0;

            return (
              <div
                key={item.id}
                className="top3-card"
                style={{
                  ...theme.wrap,
                  borderRadius: 14,
                  padding: 18,
                  marginTop: mt,
                }}
              >
                <Card
                  item={item}
                  theme={theme}
                  setSelectedInfo={setSelectedInfo}
                  setSelectedRank={setSelectedRank}
                  onHide={handleHideCard}
                />
              </div>
            );
          })}
        </div>
      )}

      {/* ====== Search Bar ====== */}
      <div style={{ textAlign: "center", marginBottom: "20px" }}>
        <input
          type="text"
          className="modern-input"
          placeholder="Search visible cards..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            width: "60%",
            maxWidth: "400px",
            padding: "10px 12px",
            fontSize: "1rem",
            borderRadius: "8px",
            border: "1px solid rgba(255,255,255,0.2)",
            backgroundColor: "#1a1a1a",
            color: "#fff",
            outline: "none",
            transition: "all 0.2s ease-in-out",
          }}
          onFocus={(e) => (e.target.style.borderColor = "rgb(197,176,55)")}
          onBlur={(e) => (e.target.style.borderColor = "rgba(255,255,255,0.2)")}
        />
        <input
          type="number"
          min="0"
          max="10"
          placeholder="#"
          value={numInfinite}
          onChange={(e) => setNumInfinite(parseInt(e.target.value) || 0)}
          style={{
            marginLeft: "10px",
            padding: "10px 12px",
            fontSize: "1rem",
            borderRadius: "8px",
            border: "1px solid rgba(255,255,255,0.2)",
            backgroundColor: "#1a1a1a",
            color: "#fff",
            outline: "none",
            width: "80px",
          }}
        />
      </div>

      {/* ====== Tier Buckets ====== */}
      {BUCKET_ORDER.map((bucket) => {
        if (!groupedData[bucket]) return null;
        const theme = groupTheme(bucket);
        return (
          <div
            key={bucket}
            className="list-group"
            style={{ ...theme.wrap, borderRadius: 14, padding: 18 }}
          >
            <div style={{ textAlign: "center", marginBottom: 12 }}>
              <span
                className="header-title"
                style={{ ...theme.titleChip, borderRadius: 999, padding: "6px 12px" }}
              >
                {bucket}
              </span>
            </div>
            <div
              className="card-grid"
              style={{ gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))" }}
            >
              {groupedData[bucket].map((item) => (
                <Card
                  key={item.id}
                  item={item}
                  theme={theme}
                  setSelectedInfo={setSelectedInfo}
                  setSelectedRank={setSelectedRank}
                  onHide={handleHideCard}
                />
              ))}
            </div>
          </div>
        );
      })}

      {/* ====== Info Modal ====== */}
      {selectedInfo && (
        <div className="modal-overlay" onClick={() => setSelectedInfo(null)}>
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "720px" }}
          >
            <h2
              className="modal-title"
              style={{ textAlign: "center", marginBottom: "16px", color: "rgb(197,176,55)" }}
            >
              {selectedInfo.cardName}
            </h2>

            <div style={{ display: "flex", justifyContent: "center", marginBottom: "16px" }}>
              <img
                src={
                  selectedInfo.customHTML && selectedInfo.customHTML.trim() !== ""
                    ? selectedInfo.customHTML
                    : selectedInfo.cardHTML
                }
                alt={selectedInfo.cardName}
                className="modal-card-image"
                style={{
                  maxWidth: "300px",
                  borderRadius: "10px",
                  boxShadow: "0 0 10px rgba(255, 215, 0, 0.3)",
                }}
              />
            </div>

            <p
              style={{
                color: "#ddd",
                textAlign: "center",
                fontSize: "1rem",
                marginBottom: "16px",
              }}
            >
              {selectedInfo.cardDesc}
            </p>

            {/* Score Grid */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, minmax(100px, 1fr))",
                gap: "14px",
                marginBottom: "16px",
              }}
            >
              <div className="rank-value" style={{ textAlign: "center" }}>
                <strong>Reliance</strong>
                <div>{selectedInfo.relianceScore}</div>
              </div>
              <div className="rank-value" style={{ textAlign: "center" }}>
                <strong>Variants</strong>
                <div>{selectedInfo.variantsScore}</div>
              </div>
              <div className="rank-value" style={{ textAlign: "center" }}>
                <strong>Usability</strong>
                <div>{selectedInfo.usabilityScore}</div>
              </div>
              <div
                className="rank-value"
                style={{
                  textAlign: "center",
                  background: "rgba(197,176,55,0.15)",
                  borderRadius: "8px",
                }}
              >
                <strong>Average</strong>
                <div>
                  {(
                    (selectedInfo.relianceScore +
                      selectedInfo.variantsScore +
                      selectedInfo.usabilityScore) /
                    3
                  ).toFixed(2)}
                </div>
              </div>
            </div>

            {/* Related Decks */}
            <InfiniteRunnersDecks
              userId={user?.id}
              cardName={selectedInfo.cardName}
            />

            <div style={{ textAlign: "center", marginTop: "20px" }}>
              <button className="button" onClick={() => setSelectedInfo(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ====== Update Modal ====== */}
      {selectedRank && (
        <div className="modal-overlay" onClick={() => setSelectedRank(null)}>
          <div
            className="modal-content styled-update-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="modal-title">Update Ranking</h2>
            <Update
              selectedRank={selectedRank}
              updatedRank={updatedRank}
              setUpdatedRank={setUpdatedRank}
              setSelectedRank={setSelectedRank}
              setData={setRankings}
            />
            <button className="close-button" onClick={() => setSelectedRank(null)}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default MyRankings_Cards;
